/**
 * /profile
 *
 * User profile page. Editable display name, email, phone.
 * Username is read-only. Change password form. Fun stats.
 * Logout button at the bottom. Works on both mobile and desktop
 * but primarily designed for the mobile app experience.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '../api/client.js';
import { getMyStats, updateProfile, changePassword, setupMfa, confirmMfa, disableMfa } from '../api/profile.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { usePageTitle } from '../hooks/usePageTitle.js';
import './ProfilePage.css';
import AvatarUpload from '../components/AvatarUpload.jsx';
import PhoneInput from '../components/PhoneInput.jsx';
import { api } from '../api/client.js';
import SiteHeader from '../components/SiteHeader.jsx';


// Friendly timestamp for "Last verified", e.g. "September 16, 2026 at 2:08 AM".
// Returns "Never" when the user has no verification on record.
function formatMfaDate(iso) {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Never';
  const date = d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: 'numeric', minute: '2-digit',
  });
  return `${date} at ${time}`;
}

export default function ProfilePage() {
  usePageTitle('Profile');

  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();

  // Editable fields
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveError, setSaveError] = useState('');

  // Password change
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwError, setPwError] = useState('');

  // Stats
  const [stats, setStats] = useState(null);

  // MFA / two-factor auth
  // mfaMode: 'idle' | 'setup' (enrolling) | 'disabling' (confirming password)
  const [mfaMode, setMfaMode] = useState('idle');
  const [mfaSetup, setMfaSetup] = useState(null);   // { secret, qr_code_base64 } from /mfa/setup
  const [mfaCode, setMfaCode] = useState('');
  const [mfaPassword, setMfaPassword] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaError, setMfaError] = useState('');

  // Populate fields from user
  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name);
      setEmail(user.email);
      setPhone(user.phone_number || '');
    }
  }, [user]);

  // Fetch stats
  useEffect(() => {
    let cancelled = false;
    getMyStats()
      .then((s) => { if (!cancelled) setStats(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const hasProfileChanges = user && (
    displayName.trim() !== user.display_name ||
    email.trim().toLowerCase() !== user.email.toLowerCase() ||
    phone.trim() !== (user.phone_number || '')
  );

  async function handleSaveProfile(e) {
    e.preventDefault();
    if (!hasProfileChanges || saving) return;

    setSaving(true);
    setSaveMsg('');
    setSaveError('');

    const fields = {};
    if (displayName.trim() !== user.display_name) fields.display_name = displayName.trim();
    if (email.trim().toLowerCase() !== user.email.toLowerCase()) fields.email = email.trim();
    if (phone.trim() !== (user.phone_number || '')) fields.phone_number = phone.trim();

    try {
      await updateProfile(fields);
      await refreshUser();
      setSaveMsg('saved');
      setTimeout(() => setSaveMsg(''), 2000);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.detail : 'failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (!currentPw || !newPw || pwSaving) return;

    setPwSaving(true);
    setPwMsg('');
    setPwError('');

    try {
      await changePassword(currentPw, newPw);
      setPwMsg('password changed');
      setCurrentPw('');
      setNewPw('');
      setTimeout(() => setPwMsg(''), 3000);
    } catch (err) {
      setPwError(err instanceof ApiError ? err.detail : 'failed to change password');
    } finally {
      setPwSaving(false);
    }
  }

  // ── MFA handlers ──

  // Begin enrollment: fetch a secret + QR, then switch to the setup view.
  async function handleEnableMfa() {
    if (mfaBusy) return;
    setMfaBusy(true);
    setMfaError('');
    try {
      const data = await setupMfa();
      setMfaSetup(data);
      setMfaCode('');
      setMfaMode('setup');
    } catch (err) {
      setMfaError(err instanceof ApiError ? err.detail : 'failed to start MFA setup');
    } finally {
      setMfaBusy(false);
    }
  }

  // Complete enrollment by verifying a code from the authenticator app.
  async function handleConfirmMfa(e) {
    e.preventDefault();
    if (mfaCode.length !== 6 || mfaBusy) return;
    setMfaBusy(true);
    setMfaError('');
    try {
      await confirmMfa(mfaCode);
      await refreshUser();
      setMfaMode('idle');
      setMfaSetup(null);
      setMfaCode('');
    } catch (err) {
      setMfaError(err instanceof ApiError ? err.detail : 'failed to enable MFA');
    } finally {
      setMfaBusy(false);
    }
  }

  // Turn MFA off — requires the account password.
  async function handleDisableMfa(e) {
    e.preventDefault();
    if (!mfaPassword || mfaBusy) return;
    setMfaBusy(true);
    setMfaError('');
    try {
      await disableMfa(mfaPassword);
      await refreshUser();
      setMfaMode('idle');
      setMfaPassword('');
    } catch (err) {
      setMfaError(err instanceof ApiError ? err.detail : 'failed to disable MFA');
    } finally {
      setMfaBusy(false);
    }
  }

  // Abort any in-progress MFA flow and reset transient state.
  function cancelMfa() {
    setMfaMode('idle');
    setMfaSetup(null);
    setMfaCode('');
    setMfaPassword('');
    setMfaError('');
  }

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  if (!user) return null;

  const memberDays = stats
    ? Math.max(1, Math.floor((Date.now() - new Date(stats.member_since).getTime()) / 86_400_000))
    : null;

  return (
    <div className="profile-page">
      <SiteHeader />
      <div className="profile-container">
        {/* ── identity header ── */}
        <div className="profile-header">
          <div className="profile-identity-row">
            {user.avatar_url ? (
              <img
                src={`${user.avatar_url}?t=${Date.now()}`}
                alt=""
                className="profile-big-avatar-img"
              />
            ) : (
              <div className="profile-big-avatar">
                {user.display_name.slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="profile-identity">
              <div className="profile-display-name">{user.display_name}</div>
              <div className="profile-username">@{user.username}</div>
              <span className={`profile-role-badge ${user.role}`}>{user.role}</span>
            </div>
          </div>
        </div>

        {/* ── avatar upload ── */}
        <section className="profile-section">
          <h2 className="profile-section-title">Photo</h2>
          <AvatarUpload
            currentUrl={user.avatar_url}
            onUploaded={(url) => refreshUser()}
            onRemove={async () => {
              await api.del('/api/auth/me/avatar');
              refreshUser();
            }}
          />
        </section>

        {/* ── stats ── */}
        {stats && (
          <div className="profile-stats">
            <div className="profile-stat">
              <div className="profile-stat-number">{stats.comment_count}</div>
              <div className="profile-stat-label">comments</div>
            </div>
            <div className="profile-stat">
              <div className="profile-stat-number">{stats.reaction_count}</div>
              <div className="profile-stat-label">reactions</div>
            </div>
            <div className="profile-stat">
              <div className="profile-stat-number">{memberDays}</div>
              <div className="profile-stat-label">{memberDays === 1 ? 'day' : 'days'}</div>
            </div>
          </div>
        )}

        {/* ── edit profile ── */}
        <section className="profile-section">
          <h2 className="profile-section-title">Profile</h2>

          <form onSubmit={handleSaveProfile}>
            <div className="profile-field">
              <label className="profile-label">username</label>
              <input
                type="text"
                value={user.username}
                disabled
                className="profile-input disabled"
              />
              <div className="profile-hint">locked — this is your login handle</div>
            </div>

            <div className="profile-field">
              <label className="profile-label">display name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="profile-input"
              />
            </div>

            <div className="profile-field">
              <label className="profile-label">email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="profile-input"
              />
            </div>

            <div className="profile-field">
              <label className="profile-label">phone</label>
              <PhoneInput
                id="profile-phone"
                value={phone}
                onChange={setPhone}
              />
            </div>

            {saveError && <div className="profile-msg error">⚠ {saveError}</div>}
            {saveMsg && <div className="profile-msg success">✓ {saveMsg}</div>}

            <button
              type="submit"
              className="profile-btn profile-btn-primary"
              disabled={!hasProfileChanges || saving}
            >
              {saving ? 'saving…' : 'save changes'}
            </button>
          </form>
        </section>

        {/* ── change password ── */}
        <section className="profile-section">
          <h2 className="profile-section-title">Password</h2>

          <form onSubmit={handleChangePassword}>
            <div className="profile-field">
              <label className="profile-label">current password</label>
              <input
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                autoComplete="current-password"
                className="profile-input"
              />
            </div>

            <div className="profile-field">
              <label className="profile-label">new password</label>
              <input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                autoComplete="new-password"
                placeholder="at least 10 characters"
                className="profile-input"
              />
            </div>

            {pwError && <div className="profile-msg error">⚠ {pwError}</div>}
            {pwMsg && <div className="profile-msg success">✓ {pwMsg}</div>}

            <button
              type="submit"
              className="profile-btn profile-btn-secondary"
              disabled={!currentPw || !newPw || newPw.length < 10 || pwSaving}
            >
              {pwSaving ? 'changing…' : 'change password'}
            </button>
          </form>
        </section>

        {/* ── two-factor auth ── */}
        <section className="profile-section">
          <h2 className="profile-section-title">Two-Factor Authentication</h2>

          {mfaError && <div className="profile-msg error">⚠ {mfaError}</div>}

          {/* Enabled — show status + last verified + disable */}
          {user.totp_enabled && mfaMode !== 'disabling' && (
            <div className="profile-mfa">
              <div className="profile-mfa-status">
                <span className="profile-mfa-dot enabled" />
                <span className="profile-mfa-state">Enabled</span>
              </div>
              <div className="profile-mfa-verified">
                Last verified: {formatMfaDate(user.mfa_verified_at)}
              </div>
              <button
                type="button"
                className="profile-btn profile-btn-secondary"
                onClick={() => { setMfaError(''); setMfaMode('disabling'); }}
              >
                disable MFA
              </button>
            </div>
          )}

          {/* Disable confirmation — inline password */}
          {user.totp_enabled && mfaMode === 'disabling' && (
            <form className="profile-mfa" onSubmit={handleDisableMfa}>
              <div className="profile-field">
                <label className="profile-label">enter your password to disable MFA</label>
                <input
                  type="password"
                  value={mfaPassword}
                  onChange={(e) => setMfaPassword(e.target.value)}
                  autoComplete="current-password"
                  className="profile-input"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                className="profile-btn profile-btn-danger"
                disabled={!mfaPassword || mfaBusy}
              >
                {mfaBusy ? 'disabling…' : 'confirm disable'}
              </button>
              <button
                type="button"
                className="profile-btn profile-btn-secondary"
                onClick={cancelMfa}
                disabled={mfaBusy}
              >
                cancel
              </button>
            </form>
          )}

          {/* Disabled — show status + enable */}
          {!user.totp_enabled && mfaMode !== 'setup' && (
            <div className="profile-mfa">
              <div className="profile-mfa-status">
                <span className="profile-mfa-dot disabled" />
                <span className="profile-mfa-state">Disabled</span>
              </div>
              <button
                type="button"
                className="profile-btn profile-btn-primary"
                onClick={handleEnableMfa}
                disabled={mfaBusy}
              >
                {mfaBusy ? 'starting…' : 'enable MFA'}
              </button>
            </div>
          )}

          {/* Setup in progress — QR + manual key + code entry */}
          {!user.totp_enabled && mfaMode === 'setup' && mfaSetup && (
            <form className="profile-mfa" onSubmit={handleConfirmMfa}>
              <p className="profile-mfa-instructions">
                Scan this QR code with your authenticator app (Bitwarden, Google
                Authenticator, etc.)
              </p>
              <img
                className="profile-mfa-qr"
                src={`data:image/png;base64,${mfaSetup.qr_code_base64}`}
                alt="MFA QR code"
                width={200}
                height={200}
              />
              <div className="profile-mfa-key-label">or enter this key manually:</div>
              <code className="profile-mfa-key">{mfaSetup.secret}</code>

              <div className="profile-field">
                <label className="profile-label">6-digit code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="profile-input profile-mfa-code"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="profile-btn profile-btn-primary"
                disabled={mfaCode.length !== 6 || mfaBusy}
              >
                {mfaBusy ? 'verifying…' : 'verify & enable'}
              </button>
              <button
                type="button"
                className="profile-btn profile-btn-secondary"
                onClick={cancelMfa}
                disabled={mfaBusy}
              >
                cancel
              </button>
            </form>
          )}
        </section>

        {/* ── logout ── */}
        <section className="profile-section profile-section-logout">
          <button
            className="profile-btn profile-btn-logout"
            onClick={handleLogout}
          >
            sign out
          </button>
        </section>
      </div>
    </div>
  );
}
